import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMergeRequest,
  approveMerge,
  rejectMerge,
  closeMergeWizard,
} from '../store/slices/identitySlice';

const providerIcons = {
  google: (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  salesforce: (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.27 1.335-.42 2.07-.42 2.73 0 4.95 2.22 4.95 4.95s-2.22 4.95-4.95 4.95c-.39 0-.78-.045-1.14-.135a3.78 3.78 0 01-3.39 2.085c-.6 0-1.17-.135-1.68-.39a4.27 4.27 0 01-3.84 2.37c-1.995 0-3.72-1.365-4.2-3.3a4.245 4.245 0 01-3.375-4.155c0-2.325 1.89-4.215 4.215-4.215.195 0 .39.015.585.045a4.19 4.19 0 014.02-2.685z"/>
    </svg>
  ),
};

function MergeAccountsModal({ mergeId, onClose }) {
  const dispatch = useDispatch();
  const { currentMerge, currentMergeLoading, currentMergeError } = useSelector(
    (state) => state.identity
  );
  const { user } = useSelector((state) => state.auth);
  const [mergeComplete, setMergeComplete] = useState(false);

  useEffect(() => {
    if (mergeId) {
      dispatch(fetchMergeRequest({ mergeId }));
    }
  }, [dispatch, mergeId]);

  const handleApprove = async () => {
    // Always merge into the current user (the one who initiated the link)
    // Note: Redux stores user ID as 'id', not 'user_id'
    const result = await dispatch(approveMerge({
      mergeId,
      targetUserId: user?.id,
      fieldChoices: {},
    }));

    if (!result.error) {
      setMergeComplete(true);
    }
  };

  const handleReject = async () => {
    await dispatch(rejectMerge({ mergeId, reason: 'User declined merge' }));
    onClose();
  };

  const handleClose = () => {
    if (mergeComplete) {
      // Reload the page to refresh session/tenants
      window.location.href = '/settings';
    } else {
      onClose();
    }
  };

  if (!mergeId) return null;

  // Determine which user is which
  const isSourceUser = currentMerge?.source_user?.user_id === user?.user_id;
  const otherUser = isSourceUser ? currentMerge?.target_user : currentMerge?.source_user;
  const currentUserInfo = isSourceUser ? currentMerge?.source_user : currentMerge?.target_user;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {mergeComplete ? 'Accounts Merged!' : 'Merge Accounts'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Loading state */}
        {currentMergeLoading && !currentMerge && (
          <div className="py-8 text-center text-gray-500">
            Loading merge request...
          </div>
        )}

        {/* Error state */}
        {currentMergeError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-4">
            {currentMergeError}
          </div>
        )}

        {/* Merge complete state */}
        {mergeComplete && (
          <div className="py-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-700 mb-4">
              Your accounts have been merged. All identities and tenants are now accessible from your account.
            </p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              Continue
            </button>
          </div>
        )}

        {/* Merge request display */}
        {currentMerge && !mergeComplete && (
          <>
            <p className="text-sm text-gray-600 mb-6">
              The account you tried to link already exists. Would you like to merge it with your current account?
            </p>

            {/* Current account */}
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Your Current Account
              </div>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="font-medium text-gray-900">{currentUserInfo?.email}</div>
                {currentUserInfo?.identities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {currentUserInfo.identities.map((id) => (
                      <span
                        key={id.identity_id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600"
                      >
                        {providerIcons[id.provider]}
                        {id.email}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center my-2">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>

            {/* Account to merge */}
            <div className="mb-6">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Account to Merge
              </div>
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="font-medium text-gray-900">{otherUser?.email}</div>
                {otherUser?.identities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {otherUser.identities.map((id) => (
                      <span
                        key={id.identity_id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded text-xs text-gray-600"
                      >
                        {providerIcons[id.provider]}
                        {id.email}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* What will happen */}
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="text-sm font-medium text-yellow-800 mb-2">What will happen:</div>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• All linked identities will be combined</li>
                <li>• Tenant memberships will be merged</li>
                <li>• You can switch between all tenants</li>
                <li>• The other account will be deactivated</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                disabled={currentMergeLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={currentMergeLoading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {currentMergeLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Merging...
                  </>
                ) : (
                  'Merge Accounts'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MergeAccountsModal;
